<template>
  <section itemscope itemtype="http://schema.org/Course" class="course">
    <template v-if="isContentLoading || !course">
      <CourseOverviewSkel :gradient="gradient" />
      <CourseContentSkel />
    </template>
    <template v-else>
      <article
        class="course__overview-article"
        :style="
          `background-image: url(${$getCourseBackground(
            course.id,
            getBackgroundSize()
          )}), linear-gradient(${gradient})`
        "
      >
        <Overview
          class="course__overview"
          :course="course"
          :progress="progressPercentage"
          :isAuthenticated="
            isUser || (isAuthenticated && course.has_sample_content)
          "
          :plansLink="checkoutLink"
          :isComingSoon="courseStatus === 'coming soon'"
          :isFree="course.has_sample_content"
          :rating="rating"
          :isRatingLoading="isRatingLoading"
          @rate-course="openRatingModal"
        />
      </article>
      <div class="course__dashboard-bg">
        <article class="course__details-article">
          <Details
            class="course__details"
            :course="course"
            :progress="progressPercentage"
            @resume="resumeCourse"
          />
        </article>
        <article
          :class="[
            'course__content-article',
            !isAuthenticated && 'course__content-article--anonymous',
          ]"
        >
          <div
            ref="groupdropdown"
            class="course__group-dropdown"
            :class="{ 'course__group-dropdown--sticky': isDropdownSticky }"
          >
            <h5
              class="course__group-dropdown-title"
              v-html="currentGroupName"
            ></h5>
            <Dropdown
              class="course__options-wrapper"
              :options="courseSectionNames"
              :searchable="false"
              :clearable="false"
              :handleSelection="handleContentSelection"
              :selectedOption="selectedContent"
            />
          </div>
          <div ref="groupWrapper" class="course__content">
            <Group
              v-for="(group, i) in courseGroups"
              :key="`course-group-${i}`"
              :group="group"
              :isUser="isUser"
              :progress="progress"
              :currentVideos="videos"
              :currentQuizzes="quizzes"
              :currentExercises="exercises"
              :currentLabs="labs"
              :currentIFrames="iframes"
              :courseId="course.id"
            />
          </div>
        </article>
      </div>

      <CpeBanner
        v-if="isCpeProgramEnabled && isOver75PercentCompleted"
        :content="course"
        :contentType="CONSTANTS.CONTENT_TYPES.COURSE"
        :isCompleted="progressPercentage === 100"
        canRateContent
        :isRatingConfirmed="isRatingConfirmed"
        @open-rating-modal="openRatingModal"
      />
      <RatingModal
        v-if="isRatingModalOpen"
        :modelId="contentRatingPayload.model_id"
        :modelType="contentRatingPayload.model_type"
        :modelName="course.name"
        :instructor="course.instructors[0]"
        :rating="rating"
        @close="closeRatingModal"
        @close-and-refresh="closeRatingModalAndRefresh"
      />
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { hasBaseAccess, getBackgroundSizeBaseOnBreakpoint } from '@/helpers'
import CourseOverviewSkel from '@/components/loaders/skeletons/course/CourseOverviewSkel.vue'
import CourseContentSkel from '@/components/loaders/skeletons/course/CourseContentSkel.vue'
import Overview from '@/components/course/Overview.vue'
import Details from '@/components/course/Details.vue'
import Group from '@/components/course/content/Group.vue'
import CONSTANTS from '@/constants'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import RatingModal from '@/components/modals/RatingModal.vue'
import CpeBanner from '@/components/banners/CpeBanner.vue'
import ratingContentMixin from '@/helpers/mixins/ratingContentMixin'

export default {
  name: 'Course',
  components: {
    CourseOverviewSkel,
    CourseContentSkel,
    Overview,
    Details,
    Group,
    RatingModal,
    CpeBanner,
    Dropdown,
  },
  mixins: [ratingContentMixin],
  inject: ['getBreakpoint'],
  metaInfo() {
    const { course } = this
    return {
      title: course && course.name ? `INE - ${course.name}` : `INE - Course`,
      meta: [
        {
          vmid: `course`,
          name: `description`,
          content: course && course.description ? course.description : '',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${CONSTANTS.MARKETING_WEBSITE_DIRECTORY_BASE_URL}/courses/${this.$route?.params?.slug}`,
        },
      ],
    }
  },
  data() {
    return {
      isLoading: true,
      levelUuids: null,
      selectedContent: { label: 'Jump to category' },
      courseSectionNames: [],
      groupsOffset: [],
      currentGroupName: '',
      CONSTANTS,
      isDropdownSticky: false,
    }
  },
  computed: {
    ...mapState({
      isContentLoading: state => state.content.isContentLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
      course: state => state.courses.currentCourse,
      allLevels: state => state.courses.currentLevels,
      videos: state => state.content.currentVideos,
      quizzes: state => state.content.currentQuizzes,
      exercises: state => state.content.currentExercises,
      labs: state => state.content.currentLabs,
      iframes: state => state.content.currentIFrames,
      progress: state => state.courses.currentCourseProgress,
      focusedLabId: state => state.labs.focusedLabId,
      currentSubscriptions: state => state.subscriptions.currentSubscriptions,
      resumeExerciseId: state => state.exercises.resumeExerciseId,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
      isCpeProgramEnabled: 'auth/isCpeProgramEnabled',
    }),
    isOver75PercentCompleted() {
      return this.progressPercentage >= 75
    },
    courseStatus() {
      return this.course.status
    },
    checkoutLink() {
      return `${process.env.VUE_APP_CHECKOUT_URL}?product_selected=ine_premium`
    },
    showProgress() {
      const { has_trackable_content } = this.course
      return this.isUser && has_trackable_content
    },
    courseGroups() {
      let index = 1
      const courseGroups = this.course?.content
      courseGroups?.forEach(group => {
        group.content?.forEach(topic => {
          topic.id = `${index++}-${topic.name
            .toLowerCase()
            .split(' ')
            .join('-')}`
        })
      })
      return courseGroups
    },
    progressPercentage() {
      const {
        progress: { progress },
      } = this
      if (progress) {
        return progress === 1 ? 100 : parseInt(progress * 100)
      }
      return 0
    },
    gradient() {
      return this.$getGradient()
    },
    hasProgress() {
      const { progress } = this
      return !!(progress && progress.is_active)
    },
    files() {
      const { files } = this.course
      return files || []
    },
    isUser() {
      return hasBaseAccess(this.course)
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.queryData(to.params.id)
      }
    },
  },
  async created() {
    await this.queryData(this.$route.params.id)
    if (this.isAuthenticated) {
      this.getRunningLab()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  destroyed() {
    this.resetContentState()
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    ...mapActions('app', ['setError']),
    ...mapActions('exercises', ['setResumeExerciseId']),
    ...mapActions('labs', ['getRunningLab', 'setFocusLabId']),
    ...mapActions('iframes', ['setPlayingIFrameId']),
    ...mapActions('courses', [
      'getActiveCourses',
      'getCourseById',
      'resetCurrentLevels',
    ]),
    ...mapActions('content', [
      'fetchContentForCourse',
      'getBookmarks',
      'setIsContentLoading',
      'resetContentState',
      'setPlayingVideoId',
      'setPlayingVideoTime',
      'setCurrentExercisesId',
      'setCurrentQuizId',
    ]),
    resumeCourse() {
      const {
        allLevels: levels,
        videos,
        quizzes,
        exercises,
        iframes,
        labs,
      } = this
      let tempVideo = null
      let tempIFrame = null
      let tempLab = null
      let tempQuizz = null
      let tempExerciseGroup = null

      let unstartedLevel = null
      let unstartedLevelType = null

      let index = 0

      while (!unstartedLevel && index < levels.length) {
        const level = levels[index]
        switch (level.content_type) {
          case CONSTANTS.CONTENT_TYPES.VIDEO:
            tempVideo = videos.find(
              video =>
                video.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                video.id === level.uuid
            )
            if (tempVideo) {
              unstartedLevel = tempVideo
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.VIDEO
            }
            break
          case CONSTANTS.CONTENT_TYPES.QUIZ:
            tempQuizz = quizzes.find(
              quiz =>
                quiz.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                quiz.id === level.uuid
            )
            if (tempQuizz) {
              unstartedLevel = tempQuizz
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.QUIZ
            }
            break
          case CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP:
            this.levelUuids = level.exercises.map(e => e.uuid)
            tempExerciseGroup = exercises.find(this.checkExerciseInGroup)
            if (tempExerciseGroup) {
              unstartedLevel = level
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP
            }
            break
          case CONSTANTS.CONTENT_TYPES.IFRAME:
            tempIFrame = iframes.find(
              iframe =>
                iframe.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                iframe.id === level.uuid
            )
            if (tempIFrame) {
              unstartedLevel = tempIFrame
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.IFRAME
            }
            break
          case CONSTANTS.CONTENT_TYPES.LAB:
            tempLab = labs.find(
              lab =>
                lab.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                lab.id === level.uuid
            )
            if (tempLab) {
              unstartedLevel = tempLab
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.LAB
            }
            break
        }
        index++
      }

      switch (unstartedLevelType) {
        case CONSTANTS.CONTENT_TYPES.VIDEO:
          this.setPlayingVideoId(unstartedLevel.id)
          this.setPlayingVideoTime(0)
          this.setPlayingIFrameId('')
          this.setCurrentExercisesId('')
          this.setCurrentQuizId('')
          break
        case CONSTANTS.CONTENT_TYPES.QUIZ:
          this.setCurrentQuizId(unstartedLevel.id)
          this.setPlayingVideoId('')
          this.setPlayingVideoTime(0)
          this.setPlayingIFrameId('')
          this.setCurrentExercisesId('')
          break
        case CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP:
          this.setCurrentQuizId('')
          this.setPlayingVideoId('')
          this.setPlayingVideoTime(0)
          this.setPlayingIFrameId('')
          this.setCurrentExercisesId(unstartedLevel.uuid)
          break
        case CONSTANTS.CONTENT_TYPES.IFRAME:
          this.setPlayingIFrameId(unstartedLevel.id)
          this.setPlayingVideoId('')
          this.setPlayingVideoTime(0)
          this.setCurrentExercisesId('')
          this.setCurrentQuizId('')
          break
        default:
          // for Labs only scroll
          this.$scrollTo('.level:not(.finished)', 1000, {
            container: 'body',
            offset: -220,
          })
          break
      }
    },
    useRatingMixin() {
      this.contentRatingPayload = {
        model_id: this.course.id,
        model_type: CONSTANTS.CONTENT_TYPES.COURSE,
      }
      this.fetchRating()
    },
    async fetchCourse(id) {
      const course = await this.getCourseById(id)
      if (!course) {
        this.$router.push({
          name: 'NotFound',
          params: { pathMatch: this.$route.path },
        })
        throw new Error('NotFound')
      }
      this.setParentContent({ ...course, type: 'course' })
      if (this.isAuthenticated) {
        this.getActiveCourses()
        this.getBookmarks()
        this.useRatingMixin()
      }
      this.setCourseSectionNames()

      await this.fetchContentForCourse({
        course,
        isAuthenticated: this.isAuthenticated,
      })
    },
    async queryData(id) {
      try {
        this.setIsContentLoading(true)

        await this.fetchCourse(id)
      } catch ({ message }) {
        this.setError(message)
      } finally {
        this.setIsContentLoading(false)
      }
      await this.$nextTick()

      if (this.resumeExerciseId) {
        this.resumeCourse()
        this.setResumeExerciseId('')
      }
      // If there is a focused Lab, scroll to it
      if (this.focusedLabId) {
        this.$scrollTo('.lab-focused', 1000, {
          container: 'body',
          offset: -220,
        })
        this.setFocusLabId('')
      }
    },
    checkExerciseInGroup(exercise) {
      return (
        exercise.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
        this.levelUuids.includes(exercise.id)
      )
    },
    handleContentSelection(selectedContent) {
      this.selectedContent = selectedContent
      const element = document.getElementById(this.selectedContent.id)
      const headerOffset = window.innerWidth <= 768 ? 165 : 135
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    },
    setCourseSectionNames() {
      let index = 1
      this.courseSectionNames = this.course?.content?.flatMap(group =>
        group.content.map(course => {
          return {
            label: course.name,
            id: `${index++}-${course.name
              .toLowerCase()
              .split(' ')
              .join('-')}`,
          }
        })
      )
    },
    handleGroupOffset(group) {
      this.groupsOffset.push(group)
    },
    handleScrollGroupNames() {
      const groups = [...document.querySelectorAll('.group')]
      const windowOffset = window.pageYOffset + 90
      const nearestGroup = groups.find(group => {
        const offset = window.pageYOffset + group.getBoundingClientRect().top
        const height = group.clientHeight
        return windowOffset > offset && windowOffset < offset + height
      })
      this.currentGroupName =
        nearestGroup?.firstChild.innerHTML || this.currentGroupName
    },
    getBackgroundSize() {
      const breakpoint = this.getBreakpoint()
      return getBackgroundSizeBaseOnBreakpoint(breakpoint)
    },
    handleScroll() {
      if (this.isContentLoading) return
      const groupDropdown = this.$refs.groupdropdown
      const courseContent = this.$refs.groupWrapper
      if (!groupDropdown || !courseContent) return
      const dropdownY = courseContent.getBoundingClientRect().top
      this.isDropdownSticky = dropdownY < 65
      if (this.isDropdownSticky) {
        this.handleScrollGroupNames()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.course {
  &__overview {
    max-width: 1700px;
    margin: 0 auto;
    &-article {
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;

      padding: 60px 20px;
      @include tablet {
        padding: 120px 100px 75px;
      }
    }
  }
  &__dashboard-bg {
    background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
    background-color: var(--dark-gray);
    background-size: cover;
  }
  &__details {
    max-width: 1700px;
    margin: 0 auto;

    &-article {
      padding: 20px;
      @include desktop {
        position: relative;
        top: -60px;
        padding: 0 100px;
      }
    }
  }
  &__content {
    max-width: 1700px;
    margin: 0 auto;
    &-article {
      position: relative;
      padding: 0 5px;
      @include tablet {
        padding: 0 40px;
      }
      @include desktop {
        padding: 0 100px 10px;
      }
      &--anonymous {
        @include tablet {
          padding: 50px 40px 0 40px;
        }
        @include desktop {
          padding: 100px 100px 0 100px;
        }
      }
    }
  }
  &__group-wrapper {
    position: relative;
    padding-top: 45px;
    @include mobile {
      padding-top: 0;
    }
  }
  &__group-dropdown {
    position: absolute;
    top: -15px;
    z-index: 100;
    right: 0;

    width: 100%;
    margin: 0 auto;
    padding-right: 20px;
    height: 63px;

    display: flex;
    align-items: center;
    justify-content: flex-end;
    @include desktop {
      padding: 0 100px;
    }

    &-title {
      display: none;
      font-size: rem(20);
      line-height: 25px;
      color: var(--white);
      margin: 0 0 10px 0;
      font-family: var(--font-regular);
      @include mobile {
        font-size: rem(32);
        line-height: 46px;
        margin: 0;
      }
    }
    &--sticky {
      justify-content: space-between;
      padding: 0 5px;

      position: fixed;
      top: 63px;
      background: var(--black);
      @include tablet {
        padding: 0 40px;
      }
      @include desktop {
        padding: 0 100px;
      }
      @include lg-desktop {
        .course__group-dropdown-container {
          max-width: 1700px;
          margin: auto;
          width: 100%;
          display: flex;
          justify-content: flex-end;
        }
      }
      .course__group-dropdown-title {
        display: block;
      }
      .course__group-dropdown-container {
        justify-content: space-between;
      }
    }
  }
  &__group-dropdown-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    @include mobile {
      flex-direction: row;
    }
  }
  &__options-wrapper {
    position: relative;
    height: 46px;
  }
  &__loader-wrapper {
    margin: 50px auto;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
